// The ONLY way this app opens a PDF with pdf.js.
//
// pdf.js is served from this origin, under /pdfjs/<version>/, by the build step in
// esbuild.config.mjs — not from cdnjs, and not bundled into application.js.
//
// WHY NOT A CDN. pdf.js 2.10.377 came from cdnjs, which put every visitor's IP and
// referrer in a third party's logs and let that third party run code in our origin.
// It was also vulnerable to CVE-2024-4367 (arbitrary JavaScript from a crafted font,
// fixed in 4.2.67). This app renders PDFs that users upload — receipts, sick notes,
// plans — in other users' browsers, so that bug was reachable by any uploader.
//
// WHY NOT BUNDLED. application.js loads on every page and is already large; most
// pages never show a PDF. Importing on first use keeps pdf.js off those pages
// entirely — an improvement on the CDN tag, which every page downloaded regardless.
//
// WHY THE LEGACY BUILD. The modern build calls Promise.withResolvers,
// Math.sumPrecise and Uint8Array.fromBase64 unguarded, which breaks older phones and
// tablets — and the workers layout serves construction staff on exactly those. The
// legacy build ships core-js polyfills for them.
//
// WHY AN UNDIGESTED, VERSIONED PATH. pdf.js fetches helper files at runtime by fixed
// filename: the JBIG2 and OpenJPEG image decoders (wasm, plus JS fallbacks), standard
// font data, CMaps and ICC profiles. Propshaft would digest those names and every
// lookup would 404. JBIG2 is the usual compression for black-and-white scans, so a
// scanned sick note would render as a blank page. The version in the path does the
// cache-busting a digest would have done.

// Injected by esbuild from node_modules/pdfjs-dist/package.json, so the URL can never
// drift from the files the build actually copied.
/* global PDFJS_VERSION */
const BASE = `/pdfjs/${PDFJS_VERSION}`

let pdfjs = null

// One import per page, however many PDFs it opens — a gallery renders a thumbnail per
// document, and each would otherwise race to load and configure the library.
//
// A failed import is NOT cached. Otherwise one dropped request on a site connection
// would leave every later PDF on that page failing until a full reload.
function library() {
  pdfjs ||= import(`${BASE}/pdf.min.mjs`)
    .then((lib) => {
      lib.GlobalWorkerOptions.workerSrc = `${BASE}/pdf.worker.min.mjs`
      return lib
    })
    .catch((error) => {
      pdfjs = null
      throw error
    })
  return pdfjs
}

// Resolves to the loaded PDFDocumentProxy. Rejects if the library cannot be fetched or
// the document cannot be parsed — callers render their own error state.
export async function loadPdf(url) {
  const lib = await library()

  return lib.getDocument({
    url,
    wasmUrl: `${BASE}/wasm/`,
    standardFontDataUrl: `${BASE}/standard_fonts/`,
    cMapUrl: `${BASE}/cmaps/`,
    cMapPacked: true,
    iccUrl: `${BASE}/iccs/`,
  }).promise
}
